/**
 * The administration cache keys an EAGER caller names, and the one reason they are not beside the
 * hooks that read them.
 *
 * **They live in the eagerly-loaded half because a WRITE has to reach them.** `api/invalidation.ts`
 * declares what each route changes, and several of those changes are administration views — a batch
 * generated from the passes screen changes the pass-stock panel, acknowledging an alert changes the
 * figure above the list. That table is reached from `useApiMutation`, which every screen uses, so it
 * cannot import anything from `features/admin/`: the build places a module shared between an
 * administration screen and a gate screen in the EAGER chunk, and pulling the administration hooks
 * (and the nine screens' worth of imports behind them) across that boundary is exactly what the
 * bundle's three gzip budgets exist to refuse.
 *
 * So the keys a write names move, and nothing else does. **The partition is the module graph's and
 * not a human's**: a key no eager caller mentions stays behind the administration chunk, in
 * `features/admin/queries.ts`, which merges the two into the one `adminKeys` every screen already
 * imports. That keeps exactly ONE spelling of every key — the whole point of having a registry,
 * after the revisions list was once spelled at its query and again at its invalidation and the two
 * drifted — while paying the eager ceiling only for the keys that have to be there.
 */

/** The cache keys, in one place, so an invalidation cannot miss a query by misspelling it. */
export const adminKeys = {
  users: () => ['user.list'] as const,
  settings: () => ['setting.list'] as const,
  apiKeys: () => ['api_key.list'] as const,
  /**
   * One panel, whatever it was asked with.
   *
   * A write invalidates a panel without knowing the range the screen happens to be showing, and
   * TanStack matches by prefix — so this is the key an effect names and {@link adminKeys.panel} is
   * the key a query is stored under. Spelling the prefix inline at each effect is how one of them
   * ends up as `['analytics','security',…]` and stops matching.
   */
  panelPrefix: (panel: string) => ['analytics', panel] as const,
  exports: () => ['export.list'] as const,
  backups: () => ['backup.list'] as const,
  maintenance: () => ['system.maintenance_status'] as const,
  /**
   * The open alert set. Takes the flag, because "open" and "open plus already seen" are two answers
   * and caching them under one key would show whichever was fetched last.
   */
  alerts: (includeAcknowledged: boolean) => ['alert.list', includeAcknowledged] as const,
  /** Every alert query, for an effect that has to invalidate both answers at once. */
  alertsPrefix: () => ['alert.list'] as const,
  /** One page of the incident log; the cursor is in the key, so paging is a different query. */
  incidents: (query: unknown) => ['presence.incidents', JSON.stringify(query)] as const,
  /** Every incident page, for a frame that marks the log stale without naming a page. */
  incidentsPrefix: () => ['presence.incidents'] as const,
  /** The action vocabulary. Effectively static, so it is fetched once and held. */
  auditActions: () => ['audit.actions'] as const,
  rollCall: () => ['presence.rollcall'] as const,
  /**
   * One day's dining roster. The date and the filter are in the key, because they are two different
   * questions and caching them under one would show whichever was fetched last.
   */
  diningRoster: (query: unknown) => ['dining.roster', JSON.stringify(query)] as const,
  /** Every roster query, for a write that marks the day stale without naming a date or a filter. */
  diningRosterPrefix: () => ['dining.roster'] as const,
  outsideNow: () => ['presence.outside'] as const,
  batches: () => ['credential_batch.list'] as const,
  reconciliation: () => ['presence.reconcile_candidates'] as const,
} as const;
